'use client'

import React from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

const accentColorMap = {
  lime: { bg: 'bg-[var(--color-lime)]', border: 'border-[var(--color-lime)]', text: 'text-[var(--color-lime)]' },
  cyan: { bg: 'bg-[var(--color-cyan)]', border: 'border-[var(--color-cyan)]', text: 'text-[var(--color-cyan)]' },
  pink: { bg: 'bg-[var(--color-pink)]', border: 'border-[var(--color-pink)]', text: 'text-[var(--color-pink)]' },
  purple: { bg: 'bg-[var(--color-purple)]', border: 'border-[var(--color-purple)]', text: 'text-[var(--color-purple)]' },
} as const

export interface StepperProps {
  currentStep: number
  steps: { title: string; description: string }[]
  variant?: 'default' | 'compact'
  accentColor?: keyof typeof accentColorMap
  className?: string
}

const Stepper: React.FC<StepperProps> = ({
  currentStep,
  steps,
  variant = 'default',
  accentColor = 'lime',
  className,
}) => {
  const colors = accentColorMap[accentColor]
  const isCompact = variant === 'compact'

  return (
    <div className={cn('w-full', isCompact ? 'mb-6' : 'mb-12', className)}>
      <div className={cn('mx-auto relative flex justify-between items-center', !isCompact && 'max-w-3xl')}>
        {/* Progress bar background */}
        <div className="absolute top-[1.25rem] left-[2.5rem] right-[2.5rem] h-[2px] bg-muted z-0" />

        {/* Progress bar active */}
        <div
          className={cn('absolute top-[1.25rem] left-[2.5rem] h-[2px] z-0 transition-all duration-500 ease-in-out', colors.bg)}
          style={{
            width: `calc(${((currentStep - 1) / (steps.length - 1)) * 100}% - 5rem)`,
          }}
        />

        {steps.map((step, index) => {
          const stepNumber = index + 1
          const isCompleted = stepNumber < currentStep
          const isCurrent = stepNumber === currentStep
          const isActive = stepNumber <= currentStep

          return (
            <div key={index} className="relative z-10 flex flex-col items-center">
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300',
                  isCompleted
                    ? cn(colors.bg, colors.border, 'text-[var(--primary-foreground)]')
                    : isCurrent
                      ? cn('bg-background', colors.border, colors.text, 'shadow-lg scale-110')
                      : 'bg-background border-muted text-muted-foreground'
                )}
              >
                {isCompleted ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <span className="text-sm font-bold">{stepNumber}</span>
                )}
              </div>
              <div className={cn('absolute whitespace-nowrap', isCompact ? 'top-10' : 'top-12')}>
                <p
                  className={cn(
                    'text-xs font-medium uppercase tracking-wider',
                    isActive ? colors.text : 'text-muted-foreground'
                  )}
                >
                  {step.title}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {!isCompact && (
        <div className="mt-20 text-center animate-in fade-in slide-in-from-bottom-4 duration-700">
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            {steps[currentStep - 1]?.description}
          </p>
        </div>
      )}
    </div>
  )
}

export { Stepper }
